import { FC, useEffect, useMemo } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
} from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useAcademicsService } from '../../../../../apis/academics/useAcademicsService';
import { IUpdateSubjectScoresRequest, IAssessmentSubject } from '../../../../../apis/academics/academicsAssessments.interfaces';
import { useAcademicsAssessmentsContext } from '../../contexts/academicsAssessments.context';
import { zNumberFromInput } from 'src/utils/zodFormHelpers';

// Note: assessmentDetailsQuery.refetch() is called in the parent component

type StudentScoreFormData = {
  scores: Array<{
    studentId: string;
    score: number;
    isApplicableForGrading: boolean;
    review?: string; // Optional review for individual student
  }>;
  remarks?: string; // Optional overall remarks
};

interface EditSubjectScoresViewProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  assessmentId: string;
  subject: IAssessmentSubject;
}

const getSubjectScoresFormSchema = (translateText: (key: string) => string, maxScore: number) =>
  z.object({
    scores: z.array(
      z.object({
        studentId: z.string().min(1),
        score: zNumberFromInput({
          min: 0,
          max: maxScore,
          maxMessage:
            translateText('academics.assessments.scoreExceedsMax') ||
            'Score cannot exceed max score',
        }),
        isApplicableForGrading: z.boolean(),
        review: z.string().optional(),
      })
    ).min(1),
    remarks: z.string().optional(),
  });

const EditSubjectScoresView: FC<EditSubjectScoresViewProps> = ({
  open,
  onClose,
  onSuccess,
  assessmentId,
  subject,
}) => {
  const { translateText } = useTranslation();
  const { updateSubjectScores } = useAcademicsService();
  const { assessmentDetailsQuery } = useAcademicsAssessmentsContext();

  const assessmentDetails = assessmentDetailsQuery.data;
  const maxScore = assessmentDetails?.maxScore || subject.scores[0]?.score || 100;

  const subjectScoresFormSchema = useMemo(
    () => getSubjectScoresFormSchema(translateText, maxScore),
    [translateText, maxScore]
  );

  const methods = useForm<StudentScoreFormData>({
    resolver: zodResolver(subjectScoresFormSchema),
    defaultValues: {
      scores: [],
      remarks: '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  // Initialize form with existing scores
  useEffect(() => {
    if (open && subject) {
      const initialScores = subject.scores.map((studentScore) => ({
        studentId: studentScore.studentDetails.id || '',
        score: studentScore.score,
        isApplicableForGrading: studentScore.isApplicableForGrading ?? true,
        review: studentScore.review || '',
      }));

      reset({
        scores: initialScores,
        remarks: subject.remarks || '',
      });
    }
  }, [open, subject, reset]);

  const onSubmit = async (data: StudentScoreFormData) => {
    try {
      const payload: IUpdateSubjectScoresRequest = {
        subject: {
          courseId: subject.courseId,
          scores: data.scores.map((score) => ({
            studentId: score.studentId,
            score: score.score,
            isApplicableForGrading: score.isApplicableForGrading,
            review: score.review || undefined,
          })),
          remarks: data.remarks || undefined,
        },
      };

      await updateSubjectScores(assessmentId, payload);
      // Refetch will be handled by parent component
      onSuccess();
    } catch (error) {
      console.error('Failed to update subject scores:', error);
      throw error;
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <StructuredModal
      isOpen={open}
      onClose={handleClose}
      title={translateText('academics.assessments.editSubjectScores') || `Edit Scores - ${subject.courseName}`}
      drawerWidth="70vw"
      footerNegativeAction={handleClose}
      footerNegativeActionText={translateText('common.cancel')}
      footerPositiveAction={handleSubmit(onSubmit)}
      footerPositiveActionText={translateText('common.update')}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {subject.courseName}
                  {subject.subjectCode && ` (${subject.subjectCode})`}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {translateText('academics.assessments.maxScore')}: {maxScore}
                </Typography>
                <Divider sx={{ mb: 2, mt: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: '600px', overflow: 'auto' }}>
                  <Controller
                    name="scores"
                    control={control}
                    render={({ field }) => (
                      <Grid container spacing={2}>
                        {field.value.map((score, index) => {
                          const studentScore = subject.scores.find(
                            (s) => s.studentDetails.id === score.studentId
                          );
                          const studentName = studentScore
                            ? `${studentScore.studentDetails.firstName} ${studentScore.studentDetails.lastName}`
                            : '';

                          return (
                            <Grid item xs={12} key={score.studentId}>
                              <Card
                                variant="outlined"
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{
                                      mb: 2,
                                      pb: 1,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    {studentName}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 40%' } }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <ControlledTextField
                                          id={`score-${score.studentId}`}
                                          name={`scores.${index}.score`}
                                          label={translateText('academics.assessments.score') || 'Score'}
                                          control={control}
                                          type="number"
                                          inputProps={{
                                            min: 0,
                                            max: maxScore,
                                            step: 0.01,
                                          }}
                                          errorMessage={errors.scores?.[index]?.score?.message}
                                          isGridItem={false}
                                          dataTestId={`score-input-${score.studentId}`}
                                        />
                                        <Controller
                                          name={`scores.${index}.isApplicableForGrading`}
                                          control={control}
                                          render={({ field: gradingField }) => (
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  {...gradingField}
                                                  checked={gradingField.value}
                                                  data-testid={`grading-checkbox-${score.studentId}`}
                                                />
                                              }
                                              label={translateText('academics.assessments.applicableForGrading') || 'Applicable for Grading'}
                                            />
                                          )}
                                        />
                                      </Box>
                                    </Box>
                                    <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 60%' } }}>
                                      <ControlledTextField
                                        id={`review-${score.studentId}`}
                                        name={`scores.${index}.review`}
                                        label={translateText('academics.assessments.studentReview') || 'Review'}
                                        control={control}
                                        multiline
                                        rows={4}
                                        placeholder={translateText('academics.assessments.optionalReviewPlaceholder') || 'Optional review for this student'}
                                        isGridItem={false}
                                        dataTestId={`review-input-${score.studentId}`}
                                      />
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  />
                  {errors.scores && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.scores.message}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  {translateText('academics.assessments.overallRemarks') || 'Overall Remarks'}
                </Typography>
                <ControlledTextField
                  id="subject-remarks"
                  name="remarks"
                  label={''}
                  control={control}
                  multiline
                  rows={3}
                  placeholder={translateText('academics.assessments.optionalOverallRemarksPlaceholder') || 'Optional overall remarks for all scores'}
                  isGridItem={false}
                  dataTestId="subject-remarks-input"
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default EditSubjectScoresView;
